import { useState, useEffect } from "react"
import { SearchArticles } from "../api" // Import the API search function

/**
 * Custom hook to handle help center search functionality using API
 * @returns {Object} - Search state and handlers
 */
const useSearch = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)

  // Function to search using the API
  const performSearch = async (term) => {
    if (!term.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)

    try {
      // Call the API search function
      const response = await SearchArticles(term)

      if (response && response.results) {
        // Transform API response to the format used by the UI
        const formattedResults = response.results.map((result) => ({
          type: "article",
          item: {
            title: result.article.name,
            slug: result.article.slug,
            _id: result.article._id,
          },
          category: result.category
            ? {
                name: result.category.name,
                slug: result.category.slug,
              }
            : null,
          displayPath: result.displayPath || [],
          slugPath: result.slugPath || [],
        }))

        setSearchResults(formattedResults)
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.error("Search error:", error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Debouncing the search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        performSearch(searchTerm)
        setShowResults(true)
      } else {
        setShowResults(false)
        setSearchResults([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Handle input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    if (!e.target.value.trim()) {
      setSearchResults([])
      setShowResults(false)
    }
  }

  // Handle search reset
  const resetSearch = () => {
    setSearchTerm("")
    setSearchResults([])
    setShowResults(false)
  }

  // Close search results
  const closeResults = () => {
    setShowResults(false)
  }

  return {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    handleSearchChange,
    resetSearch,
    closeResults,
    setShowResults,
  }
}

export default useSearch
